import { useEffect, useRef } from "react";

/**
 * Find the nearest scrollable ancestor of an element.
 * Checks for overflow auto/scroll, and also detects the implicit
 * scroll container created when overflow-x is hidden (browsers set
 * overflow-y to auto in that case).
 */
function getScrollParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
        const style = getComputedStyle(parent);
        const { overflowX, overflowY } = style;
        const isScrollable = /(auto|scroll)/.test(overflowY);
        const hasImplicitScroll = overflowX === "hidden" && overflowY === "auto";
        if (isScrollable || hasImplicitScroll) {
            // Skip if it's not actually scrollable (content fits)
            if (parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
        }
        parent = parent.parentElement;
    }
    return null;
}

/**
 * Smoothly scrolls an element into view when `shouldScroll` becomes true.
 * Uses a custom ease-in-out-cubic animation for a gentle, non-jarring motion.
 * Scrolls to reveal the element's bottom, but never past its top — so an element
 * taller than the viewport keeps its top in view instead of jumping to the bottom.
 * Works in window, scrollable containers (modals), and elements with
 * implicit scroll contexts (e.g. overflow-x: hidden on .main).
 *
 * @param shouldScroll - Whether to trigger the scroll
 * @param options.delay - Delay before scrolling starts (ms, default 150)
 * @param options.duration - Scroll animation duration (ms, default 600)
 * @param options.margin - Extra breathing room below the element (px, default 24)
 */
export function useSmoothScrollIntoView<T extends HTMLElement>(
    shouldScroll: boolean,
    options?: { delay?: number; duration?: number; margin?: number },
) {
    const ref = useRef<T>(null);
    const { delay = 150, duration = 600, margin = 24 } = options ?? {};

    useEffect(() => {
        if (!shouldScroll || !ref.current) return;

        const timer = setTimeout(() => {
            const el = ref.current;
            if (!el) return;

            const scrollParent = getScrollParent(el);
            const rect = el.getBoundingClientRect();

            let bottomOverflow: number;
            let topOffset: number;
            let start: number;
            let scrollFn: (pos: number) => void;

            if (scrollParent) {
                const containerRect = scrollParent.getBoundingClientRect();
                bottomOverflow = rect.bottom - containerRect.bottom + margin;
                topOffset = rect.top - containerRect.top - margin;
                start = scrollParent.scrollTop;
                scrollFn = (pos) => {
                    scrollParent.scrollTop = pos;
                };
            } else {
                bottomOverflow = rect.bottom - window.innerHeight + margin;
                topOffset = rect.top - margin;
                start = window.scrollY;
                scrollFn = (pos) => {
                    window.scrollTo(0, pos);
                };
            }

            // Reveal as much of the expansion as fits, but never scroll its top past the
            // top edge — otherwise a form taller than the viewport lands the user at its
            // bottom. Capping at topOffset keeps the top of the form in view.
            const delta = Math.min(bottomOverflow, topOffset);

            if (delta > 0) {
                const startTime = performance.now();
                const easeInOutCubic = (t: number) =>
                    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

                const step = (now: number) => {
                    const elapsed = now - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    scrollFn(start + delta * easeInOutCubic(progress));
                    if (progress < 1) requestAnimationFrame(step);
                };

                requestAnimationFrame(step);
            }
        }, delay);

        return () => clearTimeout(timer);
    }, [shouldScroll, delay, duration, margin]);

    return ref;
}
